// src/utils/helpers/localeUtils.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import app from 'configs/appResolver';
import { getStorageThemeItem, setStorageThemeItem } from 'stores/theme';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

const LS_LANG_KEY = 'locale';
export const FALLBACK_COUNTRY = 'US';
export const FALLBACK_LOCALE = 'en-us';

// *******************************************************************************
// NORMALIZERS
// *******************************************************************************
export const normalizeLocale = (val = '') => val.toString().trim().toLowerCase().replace('_', '-');

export const toCanonicalLocale = (val = '') => {
  const v = normalizeLocale(val);
  // Check specific locales BEFORE startsWith catch-alls to avoid pt-pt → pt-br
  if (v === 'pt-pt' || v === 'PT' || v === 'pt') return 'pt-pt';
  if (v === 'pt-br' || v === 'BR' || v.startsWith('pt-br')) return 'pt-br';
  if (v === 'en' || v === 'US' || v === 'en-us' || v.startsWith('en')) return 'en-us';
  if (v === 'es-es' || v === 'es' || v.startsWith('es')) return 'es-es';
  // hard fallback for anything outside expected range
  return FALLBACK_LOCALE;
};

export const toCanonLang = (val = '') => normalizeLocale(val);

export const toBcp47 = (val = '') => {
  const canon = toCanonLang(val);
  return canon
    .split('-')
    .map((s, i) => (i === 0 ? s : s.toUpperCase()))
    .join('-');
};

export const toCanonCountry = (val = '') => normalizeLocale(val).split('-').pop();

// *******************************************************************************
// COUNTRY -> STATIC LOCALE
// *******************************************************************************
export const COUNTRY_TO_LOCALE = {
  br: 'pt-br',
  pt: 'pt-pt',
  us: 'en-us',
  gb: 'en-gb',
  uk: 'en-gb',
  ca: 'en-ca',
  au: 'en-au',
  nz: 'en-nz',
  in: 'en-in',
  de: 'de-de',
  fr: 'fr-fr',
  es: 'es-es',
  mx: 'es-mx',
  ar: 'es-ar',
  cl: 'es-cl',
  it: 'it-it',
  jp: 'ja-jp',
  cn: 'zh-cn',
  tw: 'zh-tw',
};

// support checks
export const isKnownCountry = cc => !!COUNTRY_TO_LOCALE[normalizeLocale(cc)];
export const isKnownLocale = loc => Object.values(COUNTRY_TO_LOCALE).includes(normalizeLocale(loc));

// normalizers with fallback
export const safeAppLocale = maybeCountry => {
  const cc = toCanonCountry(maybeCountry || FALLBACK_COUNTRY);
  return COUNTRY_TO_LOCALE[cc] || FALLBACK_LOCALE;
};

export const safeAppLanguage = maybeLang => {
  const canon = toCanonLang(maybeLang || FALLBACK_LOCALE);
  return isKnownLocale(canon) ? canon : FALLBACK_LOCALE;
};

// keeps existing API; now using standard fallback
// Supports BCP 47 (pt-BR) and isolated country codes (BR)
export const getStaticLocaleFromApp = (input = app.LOCALE || FALLBACK_COUNTRY, revert = false) => {
  const v = (input || '').toString().toLowerCase();

  // Extract country code from BCP 47 (pt-br -> br) or use as-is if already country code
  const countryCode = v.includes('-') ? v.split('-').pop() : v;

  if (!revert) {
    return COUNTRY_TO_LOCALE[countryCode] || FALLBACK_LOCALE; // country -> locale
  }

  // reverse: return country code (uppercase)
  return countryCode.toUpperCase() || FALLBACK_COUNTRY;
};

// *******************************************************************************
// INITIALIZERS / PERSISTENCE
// *******************************************************************************
export const getInitialLocale = i18n => {
  const saved = typeof window !== 'undefined' ? window.localStorage.getItem('i18nextLng') : null;

  // ordem: i18nextLng -> language do storage -> app.LANGUAGE -> fallback
  const fromStorage =
    saved || (typeof window !== 'undefined' ? getStorageThemeItem('language') : null);
  const primary = fromStorage || app?.LANGUAGE || FALLBACK_LOCALE;
  const lang = safeAppLanguage(primary);

  if (i18n?.language !== lang) i18n.changeLanguage(lang).then();

  // Salva nos dois lugares
  if (typeof window !== 'undefined') {
    setStorageThemeItem('language', lang);
    localStorage.setItem('i18nextLng', lang);
  }

  return lang;
};

export const persistLanguage = lang => {
  const canon = safeAppLanguage(lang);

  if (typeof window !== 'undefined') {
    setStorageThemeItem('language', canon);
    localStorage.setItem('i18nextLng', canon);
  }

  return canon;
};

// *******************************************************************************
// HEADERS BUILDER
// *******************************************************************************
export const buildRequestLocaleHeaders = i18n => {
  const langCanon = safeAppLanguage(i18n?.language || app.LANGUAGE || FALLBACK_LOCALE); // dynamic
  const subCC = app.subscriptionCountryCode; // set by buildAppConfigurations on subscription switch
  const localecode = subCC ? safeAppLocale(subCC) : safeAppLocale(app.LOCALE); // subscription-aware
  const countryCanon = getStaticLocaleFromApp(localecode, true); // locale -> country code

  return {
    'accept-language': toBcp47(langCanon),
    'x-language': langCanon,
    'x-countrycode': countryCanon,
    'x-locale': localecode,
  };
};
